import sessionTokenStorage from "@/constants/SessionTokenStorage";
import { fetchWelcomeData } from "@/services/welcome.queries";
import { useToastNotify } from "@/stores/toastStore";
import { useUiModulesStore } from "@/stores/uiModulesStore";
import { getValueFromStorage } from "@/utils/secureStore";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { useLocalSearchParams } from "expo-router";
import * as InFlowEnums from "ipctg-inflow-types/InFlowEnums";
import { useEffect, useState } from "react";

export const useWelcomeData = () => {
  const { notify } = useToastNotify();
  const { qr } = useLocalSearchParams<{ qr?: string }>();
  const [errorNotified, setErrorNotified] = useState(false);
  const isDev = process.env.NODE_ENV === "development";
  const setFlexPassUiModule = useUiModulesStore((state) => state.setFlexPassUiModule);
  const isFocused = useIsFocused();

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ["welcome", qr], // Added qr to queryKey
    enabled: isFocused,
    queryFn: async () => {
      const activeQr = qr || (await getValueFromStorage("cachedQr")) || (isDev ? "INFLOWTEST" : undefined);
      if (!activeQr) {
        throw new Error("No QR code available");
      }
      const rsp = await fetchWelcomeData({ qr: activeQr });

      await AsyncStorage.multiSet([
        ["cachedQr", activeQr],
        ["skin", JSON.stringify(rsp.skin)],
      ]);

      sessionTokenStorage.setToken(rsp.sessionToken);

      return rsp;
    },
    retry: false,
  });

  useEffect(() => {
    if (isError && !errorNotified) {
      notify({
        severity: "error",
        message: t("An error occurred when fetching welcome data"),
      });
      console.error("Error fetching welcome data:", error);
      setErrorNotified(true);
    }
    if (!isError) {
      setErrorNotified(false);
    }
  }, [isError, error, errorNotified, notify]);

  useEffect(() => {
    if (data) {
      const bool = data.uiModules.includes(InFlowEnums.UIModule.FlexPass);
      setFlexPassUiModule(bool);
    }
  }, [data, setFlexPassUiModule]);

  return {
    data,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  };
};
